import { useContext, useEffect } from "react";
import {
  Button,
  Modal,
  Grid,
  Text,
  Divider,
  CheckboxController,
} from "@pushpress/shared-components";
import { MemberProfileFragment } from "../../../components/controllers/MemberProfileSelect/MemberProfileSelect-generated.hooks";
import MemberProfileSelect from "./../../../components/controllers/MemberProfileSelect";
import DiscountSelect from "./../../../components/controllers/DiscountSelect";
import UserBillingSelect from "./../../../components/controllers/UserBillingSelect";
import { DiscountControllerOptionFragment } from "./../../../components/controllers/DiscountSelect/DiscountSelect-generated.hooks";
import { useForm, useWatch } from "react-hook-form";
import { AppContext } from "../../../context/app";
import { useGetCalendarItemDetailQuery } from "../CalendarDetail/CalendarDetail-generated.hooks";
import { PaymentMethodControllerOptionFragment } from "../../../graphql/graphql-generated.types";
import {
  CASH_UUID,
  COMP_UUID,
  getBillingUuid,
} from "../../../components/controllers/UserBillingSelect/UserBillingSelect.utils";
import {
  defaultToastOptions,
  extractError,
  formatToUSD,
  sleep,
  toFixedConditional,
} from "../../../utils/misc";
import {
  useGetInvoicePreviewForEventQuery,
  usePurchaseCalendarItemMutation,
  useGetProfileForAddMemberModalQuery,
} from "./AddMemberToEventModal-generated.hooks";
import { toast } from "react-toastify";
import { useToggle } from "../../../hooks/state";
import { getDiscountDescription } from "../../../components/controllers/DiscountSelect/DiscountSelect.utils";

type AddMemberToEventModalProps = {
  calendarUuid: string;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  visible: boolean;
  userUuid?: string;
};

function AddMemberToEventModal(props: AddMemberToEventModalProps) {
  return (
    <Modal
      visible={props.visible}
      closeModal={props.onClose}
      verticalAlign="top"
      disableClickAway
      disableEscapeKeyDown
    >
      {props.visible && <AddMemberToEventContent {...props} />}
    </Modal>
  );
}

interface Form {
  profile?: MemberProfileFragment;
  paymentMethod?: PaymentMethodControllerOptionFragment;
  collectPaymentNow?: boolean;
  discount?: DiscountControllerOptionFragment;
}

function AddMemberToEventContent({
  onClose,
  onSuccess,
  calendarUuid,
  userUuid,
}: AddMemberToEventModalProps) {
  const [purchaseCalendarItem] = usePurchaseCalendarItemMutation();
  const [purchaseLoading, purchaseLoadingActions] = useToggle(false);
  const { control, handleSubmit, setValue } = useForm<Form>({
    mode: "onTouched",
    defaultValues: {
      collectPaymentNow: true,
    },
  });
  const { profile, paymentMethod, discount } = useWatch({
    control,
  });
  const { client } = useContext(AppContext);

  const { data: profileData } = useGetProfileForAddMemberModalQuery({
    variables: {
      clientUuid: client.uuid,
      userUuid: userUuid!,
    },
    skip: !userUuid || !client.uuid,
  });

  useEffect(() => {
    if (profileData?.getProfile) {
      setValue("profile", profileData.getProfile);
    }
  }, [profileData, setValue]);
  const { data } = useGetCalendarItemDetailQuery({
    variables: {
      getCalendarItemInput: {
        uuid: calendarUuid,
        clientUuid: client.uuid,
      },
    },
  });
  const { data: invoicePreviewData } = useGetInvoicePreviewForEventQuery({
    variables: {
      getInvoicePreviewForEventInput: {
        calendarItemUuid: calendarUuid,
        billingUuid: getBillingUuid(paymentMethod?.uuid),
        discountUuid: discount?.discountUuid,
      },
    },
    fetchPolicy: "no-cache",
    skip: !calendarUuid,
  });

  const calendarItem = data?.getCalendarItem;
  const save = async (values: Form) => {
    try {
      purchaseLoadingActions.on();
      const response = await purchaseCalendarItem({
        variables: {
          input: {
            calendarItemUuid: calendarItem.uuid,
            userUuid: values.profile?.userUuid,
            billingUuid: getBillingUuid(values.paymentMethod?.uuid),
            onSession: false,
            source: "CoreClassView",
            discountUuid: values.discount?.discountUuid,
            collectPaymentNow: values.collectPaymentNow,
            overrideRules: true,
          },
        },
      });

      switch (response.data?.purchaseCalendarItem.__typename) {
        case "CalendarItemPurchaseBillingFailedResult":
          toast.error(
            `Error billing client: ${response.data?.purchaseCalendarItem.message}`,
            defaultToastOptions,
          );
          break;
        case "CalendarItemPurchaseBookFailedResult":
          toast.error(
            `Error booking event: ${response.data?.purchaseCalendarItem.message}`,
            defaultToastOptions,
          );
          break;
        case "CalendarItemPurchaseBillingNeedActionResult":
          await sleep(1000);
          toast.success(
            `Customer successfully added to ${calendarItem?.title}. An email has been sent for them to finalize the payment process.`,
            defaultToastOptions,
          );
          onSuccess();
          break;
        case "CalendarItemPurchaseSuccessResult":
          await sleep(1000);
          toast.success(
            `Customer successfully added to ${calendarItem?.title}`,
            defaultToastOptions,
          );
          onSuccess();
          break;
      }
    } catch (err) {
      toast.error(
        `Error purchasing event: ${extractError(err)}`,
        defaultToastOptions,
      );
    } finally {
      purchaseLoadingActions.off();
    }
  };

  if (!calendarItem) return <></>;

  const invoicePreview = invoicePreviewData?.getInvoicePreviewForEvent;
  const transactionFee = invoicePreview?.fees.find(
    (fee) => fee.code === "transactionFee",
  );
  const isFree = Number(calendarItem.price) === 0;
  const showDiscount = !!discount?.discountUuid;
  const showTax = Number(calendarItem.tax?.amount) !== 0;
  const showSubTotal = showDiscount || showTax || transactionFee;

  return (
    <Modal.Card>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        paddingX={3}
        paddingY={3}
      >
        <Grid item>
          <Text variant="heading-4">Add attendee</Text>
        </Grid>
        <Grid item>
          <Grid container>
            <Grid item>
              <Button
                text="Close"
                buttonType="secondary"
                size="medium"
                onClick={onClose}
              />
            </Grid>
            <Grid item>
              <Button
                text="Save"
                buttonType="primary"
                size="medium"
                type="button"
                disabled={!profile?.userUuid}
                loading={purchaseLoading}
                onClick={handleSubmit(save)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Modal.Content>
        <MemberProfileSelect
          control={control}
          name="profile"
          label="Client"
          required
          className="mt-4 mb-0"
          placeholder="Search for a name"
          async
          menuPortalTarget={document.body}
          hideDropdownIndicator
          isClearable
        />
        {profile?.userUuid && (
          <>
            {!isFree && (
              <>
                <UserBillingSelect
                  userUuid={profile.userUuid}
                  control={control}
                  name="paymentMethod"
                  label="Payment Method"
                  menuPortalTarget={document.body}
                  required
                  className="mt-4 mb-0"
                />
                {paymentMethod?.uuid !== COMP_UUID && (
                  <DiscountSelect
                    control={control}
                    name="discount"
                    label="Discount"
                    placeholder="— Add —"
                    className="mt-4 mb-0"
                  />
                )}
              </>
            )}
          </>
        )}
        {paymentMethod?.uuid === CASH_UUID && (
          <>
            <CheckboxController
              control={control}
              name="collectPaymentNow"
              label="Collect payment now"
              className="mt-4 mb-0"
            />
            <Text variant="body-md" color="wolf-600" className="mt-1">
              Select this option to close invoice and mark it as
              &apos;paid&apos;
            </Text>
          </>
        )}
      </Modal.Content>
      <Modal.Bottom>
        {isFree && (
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Text variant="body-lg">Total</Text>
            </Grid>
            <Grid item>
              <Text variant="body-lg">Free!</Text>
            </Grid>
          </Grid>
        )}
        {paymentMethod?.uuid === COMP_UUID && (
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Text variant="body-lg">Total</Text>
            </Grid>
            <Grid item>
              <Text variant="body-lg">No charge</Text>
            </Grid>
          </Grid>
        )}
        {!isFree && paymentMethod?.uuid !== COMP_UUID && (
          <>
            {showSubTotal && (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item>
                  <Text variant="body-lg">Subtotal</Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg">
                    {formatToUSD(calendarItem.price, client.currencyIso)}
                  </Text>
                </Grid>
              </Grid>
            )}
            {showDiscount && (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="mt-1"
              >
                <Grid item>
                  <Text variant="body-lg">Discount</Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg">
                    {getDiscountDescription(
                      discount as DiscountControllerOptionFragment,
                      client.currencyIso,
                    )}
                  </Text>
                </Grid>
              </Grid>
            )}
            {showTax && (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="mt-1"
              >
                <Grid item>
                  <Text variant="body-lg">
                    Tax ({toFixedConditional(invoicePreview?.tax?.rate * 100)}%)
                  </Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg">
                    {formatToUSD(
                      invoicePreview?.tax?.amount,
                      client.currencyIso,
                    )}
                  </Text>
                </Grid>
              </Grid>
            )}
            {transactionFee && (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="mt-1"
              >
                <Grid item>
                  <Text variant="body-lg">
                    {transactionFee.description} (
                    {toFixedConditional(transactionFee.rate * 100)}%)
                  </Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg">
                    {formatToUSD(transactionFee.fee, client.currencyIso)}
                  </Text>
                </Grid>
              </Grid>
            )}
            {showSubTotal && <Divider className="mb-4 mt-4" />}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Text variant="body-lg">Total</Text>
              </Grid>
              <Grid item>
                <Text
                  variant="body-lg"
                  loading={!invoicePreview}
                  loadingCharacters={6}
                >
                  {formatToUSD(invoicePreview?.total || 0, client.currencyIso)}
                </Text>
              </Grid>
            </Grid>
          </>
        )}
      </Modal.Bottom>
    </Modal.Card>
  );
}

export default AddMemberToEventModal;
